import { isValidElement, ReactNode } from "react";
import styled from "@emotion/styled";
import { ColorProp, getColorFromTheme } from "styles/themes";

import * as customIcons from "../CustomIcons";
import * as icons from "./SVGs";

export type IconKey = keyof typeof icons;
export type CustomIconKey = keyof typeof customIcons;
export type AllIconKey = IconKey | CustomIconKey;

export const DEFAULT_ICON_SIZE = 16;

type StyleProps = {
  background?: {
    color: ColorProp;
    padding?: number;
    borderRadius?: number;
  };
  color?: ColorProp;
  size?: number;
  fill?: ColorProp;
  ariaLabel?: string;
  className?: string;
};
type SVGProps = {
  svg: ReactNode;
} & StyleProps;

export type IconProps = {
  icon: AllIconKey;
} & StyleProps;

export type UIconProps = SVGProps | IconProps;

export const ALL_ICON_KEYS = [...Object.keys(icons), ...Object.keys(customIcons)] as AllIconKey[];
const ALL_ICONS = { ...icons, ...customIcons };

export function resolveIconToReactSvg(icon: IconProps["icon"]): JSX.Element {
  const svg = ALL_ICONS[icon];
  return svg;
}

export function UIcon(props: UIconProps) {
  let svg: ReactNode = null;

  if ("svg" in props) {
    svg = props.svg;
  } else {
    svg = resolveIconToReactSvg(props.icon);
  }

  if (!svg) {
    console.error(`Icon ${svg} not found`);
    // Handle the case where the icon doesn't exist
    return null;
  }
  if (!isValidElement(svg)) {
    console.error(`Icon ${svg} is not a valid React element`);
    return null;
  }

  return (
    <StyledIcon
      as={svg.type}
      {...svg.props}
      background={props.background}
      color={props.color ?? svg.props.color}
      fill={props.fill ?? svg.props.fill}
      size={props.size ?? svg.props.size}
      className={props.className}
      aria-label={props.ariaLabel}
    />
  );
}

const StyledIcon = styled.svg<StyleProps>(({ theme, background, color, fill, size }) => ({
  width: size ?? DEFAULT_ICON_SIZE,
  height: size ?? DEFAULT_ICON_SIZE,
  fill: getColorFromTheme(fill, theme) ?? "inherit",
  color: getColorFromTheme(color, theme) ?? "inherit",
  flexShrink: 0,
  ...(background && {
    background: getColorFromTheme(background.color, theme),
    padding: background.padding ?? 4,
    borderRadius: background.borderRadius ?? 4,
    boxSizing: "content-box",
  }),
}));

export default UIcon;
